import { useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getHotelById, getPromotions } from '@/api/hotelService';
import { Hotel, Product } from '@/types';
import { Header } from '@/components/layout/Header';
import { ProductCard } from '@/components/products/ProductCard';
import { useLanguage } from '@/contexts/LanguageContext';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCart } from '@/contexts/CartContext';
import { Button } from '@/components/ui/button';
import { PromotionCarousel } from '@/components/promotions/PromotionCarousel';
import { CategoryIcon } from '@/components/ui/category-icon';
import { QuickCheckoutSheet } from '@/components/checkout/QuickCheckoutSheet';
import { WelcomeBanner } from '@/components/home/WelcomeBanner';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export default function HomePage() {
  const { hotelId } = useParams<{ hotelId: string }>();
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const query = useQuery();
  const [promotions, setPromotions] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();
  const { totalItems } = useCart();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!hotelId) {
          setError('Hotel ID is required');
          return;
        }
        
        const token = query.get('token'); 

        const hotelData = await getHotelById(hotelId, token);
        
        if (!hotelData) {
          setError('Hotel not found');
          return;
        }
        
        setHotel(hotelData);
        
        if (hotelData.products.length > 0) {
          setActiveCategory('all');
        }

        const promotionsData = await getPromotions(hotelId, token);
        setPromotions(promotionsData);
        
      } catch (err) {
        setError('Failed to load hotel data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [hotelId]);

  useEffect(() => {
    if (hotel && activeCategory === null) {
      setActiveCategory('all');
    }
  }, [hotel, t, activeCategory]);

  const handleCheckout = () => {
    setCheckoutOpen(true);
  };

  const getCategoryCount = (key: string) => {
    if (!hotel) return 0;
    if (key === 'all') return hotel.products.length;
    return hotel.products.filter(p => p.category === key).length;
  };
  

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  if (error || !hotel) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold">{error || t('error')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('pageNotFound')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const rawCategories = ['all', ...new Set(hotel.products.map(p => p.category))];
  const getCategoryLabel = (key: string) => {
    if (key === 'all') return t('allCategories');
    return key;
  };

  
  return (
    <div className="min-h-screen flex flex-col">
      <Header title={hotel.name} />
      <main className="flex-1 overflow-y-auto pb-24 hide-scrollbar">
  
        {/* PROMOZIONI */}
        {promotions.length > 0 ? (
          <div className="px-4 pt-4">
            <PromotionCarousel promotions={promotions} />
          </div>
        ): (
          <div className="px-4 pt-4">
            <WelcomeBanner hotel={hotel} />
          </div>
        )}
  
        {/* CATEGORIE E PRODOTTI */}
        {rawCategories.length > 0 && hotel.products.length > 0 ? (
          <Tabs 
            value={activeCategory || ''} 
            onValueChange={setActiveCategory}
            className="w-full"
          >
            <div className="sticky top-0 z-10 bg-background pt-2 px-4 pb-0">
              <TabsList 
                ref={tabsRef}
                style={{ scrollPaddingLeft: '1rem' }}
                className="w-full mb-0 overflow-x-auto hide-scrollbar h-auto bg-muted/50 backdrop-blur-sm"
              >
                {rawCategories.map((category) => (
                  <TabsTrigger 
                    key={category} 
                    value={category}
                    className="flex-shrink-0 min-w-max flex flex-col items-center py-2 px-4 h-auto data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg"
                  >
                    <CategoryIcon category={category} className="mb-1 h-5 w-5" />
                    <span className="text-xs font-medium">{getCategoryLabel(category)}</span>
                    <span className="text-[10px] text-muted-foreground mt-0.5">
                      {getCategoryCount(category)} {getCategoryCount(category) === 1 ? t('item') : t('items')}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
  
            {rawCategories.map((category) => {
              const categoryProducts =
              category === 'all'
                ? hotel.products
                : hotel.products.filter(product => product.category === category);
              return (
                <TabsContent key={category} value={category} className="mt-0 px-4">
                  {categoryProducts.length > 0 ? (
                    <div className="divide-y">
                      {categoryProducts.map((product) => (
                        <ProductCard 
                          key={product.id} 
                          product={product}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted-foreground text-sm py-6">
                      {t('noProductsInCategory')}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        ) : (
          <div className="text-muted-foreground text-sm px-4 py-6">
            {t('noProductsAvailable')}
          </div>
        )}
      </main>
  
      {/* BOTTONE CHECKOUT */}
      {totalItems > 0 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t safe-bottom">
          <Button 
            className="w-full h-12 text-base flex items-center justify-center"
            onClick={handleCheckout}
          >
            {t('proceedToCheckout')}
          </Button>
        </div>
      )}
      
      <QuickCheckoutSheet 
        open={checkoutOpen} 
        onOpenChange={setCheckoutOpen} 
      />
    </div>
  );
}